// src/components/AuditLogs/AuditLogsPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import styled from "styled-components";
import { useDebounce } from "../../../hooks/useDebounce";
import { formatDateTime } from "../../../utils/dateUtils";
import {
  fetchSystemLogs,
  fetchCarePlanLogs,
  fetchBuilderLogs,
  fetchUnifiedLogs,
  logSystemEvent,
} from "../../../redux/slices/auditLogsSlice";
import {
  fetchSystemAuditLogsService,
  fetchAllCarePlanAuditLogs,
  fetchBuilderAuditLogsService,
  fetchUnifiedAuditLogsService,
} from "../../../services/dashboardServices";
import ToggleSwitch from "./ToggleSwitch";
import Pagination from "../../common/Pagination";
import IconButton from "../../common/Button";
import DateRangePicker from "../../common/DateRangePicker";
import { SearchIcon, ExportUpIcon, FilterIcon } from "../../common/Icons";
import Spacer from "../../common/Spacer";

const AuditLogsPage = () => {
  const dispatch = useDispatch();
  const { all, system, carePlan, builder } = useSelector((s) => s.auditLogs);
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);

  const [showFilter, setShowFilter] = useState(false);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const filterRef = useRef();

  useEffect(() => {
    const payload = {
      page,
      pageSize: rowsPerPage,
      search: debouncedSearch,
      date_from: dateRange.from,
      date_to: dateRange.to,
    };
    switch (tab) {
      case "system":
        dispatch(fetchSystemLogs(payload));
        break;
      case "carePlan":
        dispatch(fetchCarePlanLogs(payload));
        break;
      case "builder":
        dispatch(fetchBuilderLogs(payload));
        break;
      default:
        dispatch(fetchUnifiedLogs(payload));
    }
  }, [tab, page, rowsPerPage, debouncedSearch, dateRange, dispatch]);

  const slice = { all, system, carePlan, builder }[tab];
  const data = slice.data;
  const entries = Array.isArray(data) ? data : data.results || [];
  const totalRows = data.count ?? entries.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const handleRowsPerPageChange = (newSize) => {
    setRowsPerPage(newSize);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    setPage(1);
  };

  const handleFilterToggle = () => setShowFilter((s) => !s);
  const handleDateChange = (range) => {
    setDateRange(range);
    setShowFilter(false);
    setPage(1);
  };

  // --- export handler ---
  const handleExport = async () => {
    const args = {
      page: 1,
      pageSize: totalRows,
      search: debouncedSearch,
      date_from: dateRange.from,
      date_to: dateRange.to,
    };
    const { page, pageSize, search, date_from, date_to } = args;

    const serviceMap = {
      system: fetchSystemAuditLogsService,
      carePlan: fetchAllCarePlanAuditLogs,
      builder: fetchBuilderAuditLogsService,
      all: fetchUnifiedAuditLogsService,
    };
    // pick the right fn (defaults to the “all”/unified feed)
    const serviceFn = serviceMap[tab] || serviceMap.all;

    // call it
    const response = await serviceFn(
      page,
      pageSize,
      search,
      date_from,
      date_to
    );

    // pull out the items array
    const allLogs = response.results || response;

    // shape for Excel
    const sheetData = allLogs.map((l) => {
      const row = {
        Action: l.action_output ?? l.action,
        "Action By Name": l.action_by_name,
        "Action By Email": l.action_by_email,
        "Action On": formatDateTime(l.action_on),
      };
      if (tab === "all" || tab === "builder") {
        row.Category = l.category;
      }
      return row;
    });

    // build & download
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Audit Logs");
    XLSX.writeFile(wb, `audit-logs-${tab}.xlsx`);

    // log in the sytem table as the log list is exported
    dispatch(logSystemEvent("Audit list exported"));
  };

  return (
    <div className="draftplan-container">
      <div className="search-filter-bar">
        <div className="left-section">
          <div className="search-bar">
            <div className="search-icon">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search here"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="right-section">
          <IconButton
            variant="secondary"
            state="idle"
            type="button"
            icon={<ExportUpIcon color="#004F96" />}
            onClick={handleExport}
          >
            Export list
          </IconButton>
          <IconButton
            variant="primary"
            state="idle"
            icon={<FilterIcon color="#FFF" />}
            type="button"
            width={"auto"}
            onClick={handleFilterToggle}
          >
            Filter
          </IconButton>
        </div>
      </div>

      {showFilter && (
        <FilterDropdown ref={filterRef}>
          <DateRangePicker
            initialRange={dateRange}
            onApply={handleDateChange}
            onCancel={() => setShowFilter(false)}
          />
        </FilterDropdown>
      )}

      <div className="toggle-container">
        <ToggleSwitch
          activeTab={tab}
          setActiveTab={(t) => {
            setTab(t);
            setPage(1);
          }}
        />
      </div>

      <Spacer />

      <TableWrapper>
        <AuditPageTable>
          <thead>
            <tr>
              <th>Action</th>
              {tab !== "system" && tab !== "carePlan" && (
                <th className="col-fit">Category</th>
              )}
              <th className="col-fit">Action By (Name)</th>
              <th>Action By (Email)</th>
              <th>Action On</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((l) => (
              <tr key={l.id}>
                <td>{l.action}</td>
                {tab !== "system" && tab !== "carePlan" && (
                  <td className="col-fit">{l.category}</td>
                )}
                <td className="col-fit">
                  {l.action_by_name ? l.action_by_name : "Unknown user"}
                </td>
                <td>
                  {l.action_by_email ? l.action_by_email : "Unknown email"}
                </td>
                <td>{formatDateTime(l.action_on)}</td>
              </tr>
            ))}
          </tbody>
        </AuditPageTable>
      </TableWrapper>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

export default AuditLogsPage;

// export const TableWrapper = styled.div`
//   margin-top: 24px;
//   border-radius: 12px;
//   overflow: auto;
//   -webkit-overflow-scrolling: touch;
//   border: 1px solid #e9ecef;
//   box-shadow: 0px 10px 16px rgba(230, 230, 230, 0.25);
// `;

// export const CarePlanTable = styled.table`
//   width: 100%;
//   border-collapse: collapse;

//   th,
//   td {
//     padding: 14px 16px;
//     text-align: left;
//     border-bottom: 1px solid #e9ecef;
//     font-size: 16px;
//     font-weight: 500;
//     line-height: 20px;
//   }

//   th {
//     background: #f8f9fa;
//     color: #6c757d;
//   }
// `;

const FilterDropdown = styled.div`
  position: absolute;
  top: 64px; /* adjust */
  right: 16px; /* align under button */
  background: white;
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  z-index: 1000;
  padding: 16px;
`;

// 1. Wrapper with horizontal scroll & shadow
const TableWrapper = styled.div`
  border-radius: 12px;
  overflow: auto;
  border: 1px solid #e9ecef;
  box-shadow: 0px 10px 16px rgba(230, 230, 230, 0.25);
  margin-top: 24px;
`;

// 2. Fixed-layout table with word wrapping
const AuditPageTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    padding: 14px 16px;
    text-align: left;
    border-bottom: 1px solid #e9ecef;
    font-size: 16px;
    line-height: 20px;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-all;
  }

  th {
    background: #f8f9fa;
    color: #6c757d;
    font-weight: 500;
  }

  /* Optional: constrain the email column */
  td.email-cell {
    max-width: 180px;
  }

  th.col-fit,
  td.col-fit {
    width: 120px;
    white-space: nowrap;
    overflow: break-word;
    text-overflow: ellipsis;
  }

  /* Rounded corners */
  th:first-child {
    border-top-left-radius: 12px;
  }
  th:last-child {
    border-top-right-radius: 12px;
  }
  tr:last-child td:first-child {
    border-bottom-left-radius: 12px;
  }
  tr:last-child td:last-child {
    border-bottom-right-radius: 12px;
  }
`;
